import { notifications } from "@mantine/notifications";

import { AUTO_CLOSE_TOAST_DURATION } from "#app/notifications";
import { useUndoableAction } from "#modules/onboardingV2/view/useUndoableAction";

type UseUndoableBulkDeleteParams<TData> = {
  getRowId: (row: TData) => string;
  getData: () => TData[] | undefined;
  setData: (rows: TData[]) => void;
  onCommit: (ids: string[]) => void;
  showUndoToast: (params: { id: string; count: number; onCancel: () => void }) => void;
  onUndo: () => void;
};

type PendingBulkDelete = {
  ids: string[];
  toastId: string;
};

export const useUndoableBulkDelete = <TData>({
  getRowId,
  getData,
  setData,
  onCommit,
  showUndoToast,
  onUndo,
}: UseUndoableBulkDeleteParams<TData>) => {
  const pendingActions = useUndoableAction<PendingBulkDelete>();

  const bulkDelete = (ids: string[]) => {
    const currentData = getData();
    if (!currentData || ids.length === 0) {
      return;
    }

    const idSet = new Set(ids);
    if (!currentData.some((row) => idSet.has(getRowId(row)))) {
      return;
    }

    setData(currentData.filter((row) => !idSet.has(getRowId(row))));

    const toastId = `bulk-delete-undo-${crypto.randomUUID()}`;
    const handle = pendingActions.schedule(
      { ids, toastId },
      AUTO_CLOSE_TOAST_DURATION,
      ({ ids: idsToCommit }) => onCommit(idsToCommit),
    );

    showUndoToast({
      id: toastId,
      count: ids.length,
      onCancel: () => {
        if (!handle.isCurrent()) {
          return;
        }
        pendingActions.cancel();
        setData(currentData);
        notifications.hide(toastId);
        onUndo();
      },
    });
  };

  const commitPendingBulkDelete = () => {
    pendingActions.commitNow(({ ids, toastId }) => {
      notifications.hide(toastId);
      onCommit(ids);
    });
  };

  return { bulkDelete, commitPendingBulkDelete };
};
